<template>
  <div class="folder-tree-node">
    <div
      class="folder-item d-flex align-items-center gap-2 p-2 mb-1"
      :class="{
        active:
          currentFolderId == folder.id ||
          String(currentFolderId) === String(folder.id),
        'drop-target':
          dropTargetId == folder.id ||
          String(dropTargetId) === String(folder.id)
      }"
      :style="{ paddingLeft: `${level * 20 + 8}px` }"
      @click="navigateToFolder"
      @dragover.prevent="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <Button
        v-if="hasChildren"
        :icon="folder.expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
        variant="text"
        size="small"
        class="expand-button"
        @click.stop="toggleExpansion"
      />
      <div v-else class="expand-spacer"></div>

      <i class="pi pi-folder text-warning"></i>
      <span class="grow folder-name">{{ folder.name }}</span>

      <div class="folder-actions">
        <Button
          ref="menuButton"
          icon="pi pi-ellipsis-h"
          variant="text"
          size="small"
          severity="secondary"
          @click.stop="toggleMenu"
        />
        <Popover ref="popover" :style="{ width: '200px' }">
          <div class="d-flex flex-column gap-2">
            <Button
              icon="pi pi-folder-plus"
              :label="$t('views.fileManager.createSubfolder')"
              variant="text"
              size="small"
              class="justify-content-start"
              @click="createSubfolder"
            />
            <Button
              icon="pi pi-trash"
              :label="$t('views.fileManager.deleteFolder')"
              variant="text"
              severity="danger"
              size="small"
              class="justify-content-start"
              @click="deleteFolder"
            />
          </div>
        </Popover>
      </div>
    </div>

    <div v-if="folder.expanded && hasChildren" class="folder-children">
      <FolderTreeNode
        v-for="child in folder.children"
        :key="child.id"
        :folder="child"
        :level="level + 1"
        :current-folder-id="currentFolderId"
        :expanded-folders="expandedFolders"
        :drop-target-id="dropTargetId"
        @navigate="$emit('navigate', $event)"
        @expand="$emit('expand', $event)"
        @create-subfolder="$emit('create-subfolder', $event)"
        @delete-folder="$emit('delete-folder', $event)"
        @folder-dragover="
          (event, folderId) => $emit('folder-dragover', event, folderId)
        "
        @folder-dragleave="() => $emit('folder-dragleave')"
        @folder-drop="
          (event, folderId) => $emit('folder-drop', event, folderId)
        "
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue"

const props = defineProps({
  folder: {
    type: Object,
    required: true
  },
  level: {
    type: Number,
    default: 0
  },
  currentFolderId: {
    type: [String, Number],
    default: null
  },
  expandedFolders: {
    type: Set,
    required: true
  },
  dropTargetId: {
    type: [String, Number],
    default: null
  }
})

const emit = defineEmits([
  "navigate",
  "expand",
  "create-subfolder",
  "delete-folder",
  "folder-dragover",
  "folder-dragleave",
  "folder-drop"
])

const popover = ref(null)
const menuButton = ref(null)

const hasChildren = computed(
  () => props.folder.children && props.folder.children.length > 0
)

const navigateToFolder = () => {
  emit("navigate", props.folder.id)
}

const toggleExpansion = () => {
  emit("expand", props.folder.id)
}

const toggleMenu = (event) => {
  popover.value.toggle(event)
}

const createSubfolder = () => {
  popover.value.hide()
  emit("create-subfolder", props.folder.id)
}

const deleteFolder = () => {
  popover.value.hide()
  emit("delete-folder", props.folder.id)
}

const handleDragOver = (event) => {
  event.preventDefault()
  emit("folder-dragover", event, props.folder.id)
}

const handleDragLeave = () => {
  emit("folder-dragleave")
}

const handleDrop = (event) => {
  event.preventDefault()
  emit("folder-drop", event, props.folder.id)
}
</script>
